import { AnimatePresence } from 'framer-motion';
import { type Translate } from 'next-translate';
import React, { useEffect, useMemo, useState } from 'react';
import { OverlayingPopup } from '@/components/elements/OverlayingPopup';
import { AmplitudeService } from '@/services';
import { type LandingTool } from '@/data/aiToolLandings';
import { AMPLITUDE_EVENTS } from '@/constants';
import CloseIcon from '@/public/icons/cross.svg';
import { FeatureGrid } from '../FeatureGrid';
import styles from './ToolsRows.module.scss';

interface Props {
	activeTabId: string;
	featuresData: LandingTool[];
	activeFeature: LandingTool | null;
	activePositioningId: string | null;
	t: Translate;
}

export const ToolsRows = ({
	featuresData,
	activePositioningId,
	activeTabId,
	activeFeature,
	t,
}: Props) => {
	const [openedFeature, setOpenedFeature] = useState<LandingTool | null>(null);

	useEffect(() => {
		document.body.style.overflow = openedFeature ? 'hidden' : '';
	}, [openedFeature]);

	const preparedLandingsData = useMemo(() => {
		return featuresData.map(landingBlock => {
			const landingsList = landingBlock.list;

			if (!activePositioningId) {
				return {
					...landingBlock,
					list: landingsList,
				};
			}
			const activePositioningItem = landingsList.find(
				item => item.id === activePositioningId,
			);

			if (!activePositioningItem) {
				return {
					...landingBlock,
					list: landingsList,
				};
			}

			// Move positioning from search to the first place at list
			const reorderedList = [
				activePositioningItem,
				...landingsList.filter(item => item.id !== activePositioningId),
			];

			return {
				...landingBlock,
				list: reorderedList,
			};
		});
	}, [featuresData, activePositioningId]);

	return (
		<>
			<AnimatePresence initial={false}>
				{openedFeature && (
					<OverlayingPopup>
						<div className={styles['modal']}>
							<div className={styles['modal__header']}>
								<h2 className={styles['modal__title']}>
									{t(openedFeature.title)}
								</h2>
								<button
									type='button'
									onClick={() => setOpenedFeature(null)}
									className={styles['modal__close']}
								>
									<CloseIcon />
								</button>
							</div>
							<FeatureGrid
								activeTabId={activeTabId}
								activeFeature={activeFeature}
								className={styles['positioning-grid']}
								itemClassName={styles['positioning-grid__item']}
								title={openedFeature.title}
								list={openedFeature.list}
								t={t}
							/>
						</div>
					</OverlayingPopup>
				)}
			</AnimatePresence>

			<div className={styles['rows']}>
				{preparedLandingsData.map(feature => (
					<div
						key={feature.title}
						className={styles['rows__item']}
						data-title={t(feature.title)}
					>
						<div className={styles['rows__item_header']}>
							<p className={styles['rows__title']}>{t(feature.title)}</p>
							{feature.list.length > 2 && (
								<button
									type='button'
									onClick={() => {
										AmplitudeService.logEvent(
											AMPLITUDE_EVENTS.ALL_TOOLS_FEATURE_SELECTED,
											{
												type: activeTabId,
												feature: t(feature.title),
											},
										);
										setOpenedFeature(feature);
									}}
									className={styles['rows__link']}
								>
									See all
								</button>
							)}
						</div>

						<FeatureGrid
							activeFeature={activeFeature}
							activeTabId={activeTabId}
							title={feature.title}
							list={feature.list}
							t={t}
							className={styles['rows__images']}
						/>
					</div>
				))}
			</div>
		</>
	);
};
